import type { FC, ReactNode } from 'react'

import { TriangleAlert } from 'lucide-react'
import { cn } from '@/lib/utils'

import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogMedia,
    AlertDialogTitle
} from '@/components/ui/alert-dialog'

export interface ConfirmOptions {
    title: string
    description?: ReactNode
    confirmLabel?: string
    cancelLabel?: string
    destructive?: boolean
}

interface ConfirmDialogProps extends ConfirmOptions {
    open: boolean
    onResult: (confirmed: boolean) => void
}

const ConfirmDialog: FC<ConfirmDialogProps> = ({
    open,
    onResult,
    title,
    description,
    confirmLabel = 'Confirm',
    cancelLabel = 'Cancel',
    destructive = false
}): ReactNode => {
    return (
        <AlertDialog open={open} onOpenChange={next => !next && onResult(false)}>
            <AlertDialogContent>
                <AlertDialogHeader>
                    {destructive && (
                        <AlertDialogMedia className='bg-destructive/10 text-destructive'>
                            <TriangleAlert />
                        </AlertDialogMedia>
                    )}

                    <AlertDialogTitle>
                        {title}
                    </AlertDialogTitle>

                    {description && (
                        <AlertDialogDescription>
                            {description}
                        </AlertDialogDescription>
                    )}
                </AlertDialogHeader>

                <AlertDialogFooter>
                    <AlertDialogCancel>
                        {cancelLabel}
                    </AlertDialogCancel>

                    <AlertDialogAction
                        onClick={() => onResult(true)}
                        className={cn(destructive && 'bg-destructive text-white hover:bg-destructive/90')}
                    >
                        {confirmLabel}
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    )
}

export default ConfirmDialog
